import React, { useState } from 'react'

import { withTranslation } from 'react-i18next'
import { Popper, ClickAwayListener, Button } from '@material-ui/core'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import ArrowForward from '@material-ui/icons/ArrowForward'

import { ButtonTooltip } from '../Tooltips/BPMTooltips'
import { addMonthsToPeriod, formatPeriodDisplay } from '../../utils/DateUtils'
import PeriodGrid from './PeriodGrid'
import usePeriodBounds from './usePeriodBounds'

/**
 * Page-level period selector: ◀ YYYY/MM ▶ with a grid popper.
 *
 * Controlled — the page owns the period, which is what every call site already
 * did by passing `year`/`month` down.
 *
 * Props:
 *   year, month      the selected period
 *   onChangePeriod   (year, month) => void
 *   bounds           optional PERIOD_BOUNDS source
 */
const PeriodNavigator = (props) => {
    const { year, month, onChangePeriod, bounds: boundsSource, style, t } = props
    const bounds = usePeriodBounds(boundsSource)

    const [anchorEl, setAnchorEl] = useState(null)
    // Which year the grid is showing. View state only, reset on every open.
    const [browsedYear, setBrowsedYear] = useState(null)

    const period = { year, month }
    const previousPeriod = addMonthsToPeriod(period, -1)
    const nextPeriod = addMonthsToPeriod(period, 1)

    const goTo = ({ year: aYear, month: aMonth }) => {
        setAnchorEl(null)
        onChangePeriod(aYear, aMonth)
    }

    const handleClick = (event) => {
        setBrowsedYear(null)
        setAnchorEl(anchorEl ? null : event.currentTarget)
    }

    return (
        <div style={{ display: 'flex' }}>
            <ButtonTooltip title={t('translations:Previous month')}>
                <span>
                    <Button disabled={!bounds.includes(previousPeriod)} onClick={() => goTo(previousPeriod)}>
                        <ArrowBackIcon />
                    </Button>
                </span>
            </ButtonTooltip>
            <div>
                <Button style={style} onClick={handleClick}>
                    <CalendarTodayIcon style={{ paddingRight: 5 }} />
                    {formatPeriodDisplay(period)}
                </Button>
            </div>
            <ButtonTooltip title={t('translations:Next month')}>
                <span>
                    <Button disabled={!bounds.includes(nextPeriod)} onClick={() => goTo(nextPeriod)}>
                        <ArrowForward />
                    </Button>
                </span>
            </ButtonTooltip>

            <Popper
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                popperOptions={{ onCreate: (data) => data.instance.popper.style.zIndex = 1 }}
            >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                    <div>
                        <PeriodGrid
                            year={browsedYear || year}
                            bounds={bounds}
                            onChangeYear={setBrowsedYear}
                            onSelect={(aYear, aMonth) => goTo({ year: aYear, month: aMonth })}
                            onClear={null}
                        />
                    </div>
                </ClickAwayListener>
            </Popper>
        </div>
    )
}

export default withTranslation()(PeriodNavigator)
